// What a pharmacy needs to identify the prescriber on an order: the NPI, the
// DEA registrations and controlled-substance-registration states, and a US
// mailing address. One module owns the vocabulary, the validation rules and
// the completeness verdict, so the staff write route, the read the pharmacy
// submitter gates on, Atlas and the reference pre-load cannot disagree about
// what "complete" means.
//
// "Complete" is exactly what the GoGoMeds order payload requires of a
// prescriber — an NPI plus street, city, state and zip — so the verdict is the
// go-live gate for a native submitter. DEA and CSR are carried but do not gate
// completeness: whether a controlled-substance order needs an active DEA
// registration in the member's state is the submitter's decision.

import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { US_STATES, type UsState } from '../registration/states';
import { CARE_LICENCE_JURISDICTIONS, usStateCodeSchema } from './care-call-types';

/** Who last wrote a row: a manager through the staff route, or the operator pre-load. */
export const PRESCRIBER_DETAILS_SOURCE = ['staff', 'reference_preload'] as const;
export type PrescriberDetailsSource = (typeof PRESCRIBER_DETAILS_SOURCE)[number];

/** The DB CHECK and the wire validator are built from this one pattern. */
export const NPI_PATTERN = /^[0-9]{10}$/;

/** Two letters (registrant type + surname initial) then seven digits. */
export const DEA_NUMBER_PATTERN = /^[A-Z]{2}[0-9]{7}$/;

/** `NNNNN` or `NNNNN-NNNN`. */
export const US_POSTAL_CODE_PATTERN = /^[0-9]{5}(-[0-9]{4})?$/;

/**
 * Whether `npi` is a well-formed National Provider Identifier: ten digits whose
 * last is the Luhn check digit computed over the first nine prefixed with the
 * card-issuer constant `80840` (the CMS NPI check-digit standard).
 */
export function isValidNpi(npi: string): boolean {
  if (!NPI_PATTERN.test(npi)) return false;
  const digits = `80840${npi.slice(0, 9)}`;
  let sum = 0;
  // Luhn: double every second digit counting from the rightmost of the payload.
  for (let i = 0; i < digits.length; i += 1) {
    let d = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  const check = (10 - (sum % 10)) % 10;
  return check === Number(npi[9]);
}

const US_COUNTRY_SPELLINGS = new Set(['us', 'usa', 'united states', 'united states of america']);

/** A prescriber with no country, or a US spelling of it, is a US prescriber. */
export function isUsCountry(country: string | null | undefined): boolean {
  if (country === null || country === undefined || country.trim() === '') return true;
  return US_COUNTRY_SPELLINGS.has(country.trim().toLowerCase());
}

/**
 * USPS two-letter codes beyond the fifty states and DC that a prescriber may
 * mail from: the inhabited territories, the freely associated states, and the
 * armed-forces codes. Kept here rather than in the member state list on
 * purpose — the member address screen's reach and the prescriber constraint
 * are separate decisions and do not widen together.
 */
export const US_TERRITORY_AND_MILITARY_REGIONS: readonly UsState[] = [
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'VI', name: 'U.S. Virgin Islands' },
  { code: 'GU', name: 'Guam' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'FM', name: 'Federated States of Micronesia' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'PW', name: 'Palau' },
  { code: 'AA', name: 'Armed Forces Americas' },
  { code: 'AE', name: 'Armed Forces Europe' },
  { code: 'AP', name: 'Armed Forces Pacific' },
];

/**
 * The regions a prescriber's mailing address may name: any USPS two-letter
 * state, territory, freely associated state or military code. Prescribers who
 * mail from a territory or an APO/FPO address are pharmacy-reachable, so the
 * full USPS set is allowed; the fifty states plus DC come from the shared state
 * list, the rest from {@link US_TERRITORY_AND_MILITARY_REGIONS}.
 */
export const PRESCRIBER_MAILING_REGIONS: readonly string[] = [
  ...CARE_LICENCE_JURISDICTIONS,
  ...US_TERRITORY_AND_MILITARY_REGIONS.map((region) => region.code),
];

const prescriberMailingRegionSet = new Set<string>(PRESCRIBER_MAILING_REGIONS);

/** Whether `code` is a region a prescriber mailing address may name. */
export function isPrescriberMailingRegion(code: string): boolean {
  return prescriberMailingRegionSet.has(code);
}

const STATE_CODE_BY_NAME = new Map(
  [...US_STATES, ...US_TERRITORY_AND_MILITARY_REGIONS].map((s) => [s.name.toLowerCase(), s.code]),
);

/**
 * A region as an upstream source might spell it (`ca`, ` CA `, `California`)
 * resolved to a two-letter jurisdiction code, or null when it is not one.
 */
export function normaliseUsRegion(region: string | null | undefined): string | null {
  if (region === null || region === undefined) return null;
  const trimmed = region.trim();
  if (trimmed === '') return null;
  const upper = trimmed.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper) && isPrescriberMailingRegion(upper)) return upper;
  const byName = STATE_CODE_BY_NAME.get(trimmed.toLowerCase());
  return byName !== undefined && isPrescriberMailingRegion(byName) ? byName : null;
}

/**
 * The territories where the DEA registers practitioners, and so where a
 * prescriber may hold a DEA registration or a controlled-substance
 * registration. Narrower than {@link US_TERRITORY_AND_MILITARY_REGIONS}: the
 * freely associated states and the armed-forces codes are places mail reaches,
 * not licensing jurisdictions.
 */
export const PRESCRIBER_LICENSING_TERRITORIES: readonly string[] = ['PR', 'VI', 'GU', 'AS', 'MP'];

/**
 * The jurisdictions a prescriber's DEA registration or CSR state may name: the
 * fifty states plus DC from the shared state list, and the DEA-registering
 * territories. A prescriber-side constant, like the mailing regions — the
 * member care-licence list decides nothing about prescribers.
 */
export const PRESCRIBER_JURISDICTIONS: readonly string[] = [
  ...CARE_LICENCE_JURISDICTIONS,
  ...PRESCRIBER_LICENSING_TERRITORIES,
];

const prescriberJurisdictionSet = new Set<string>(PRESCRIBER_JURISDICTIONS);

/** Whether `code` is a jurisdiction a prescriber's DEA registration or CSR state may name. */
export function isPrescriberJurisdiction(code: string): boolean {
  return prescriberJurisdictionSet.has(code);
}

export const deaRegistrationSchema = z
  .object({
    /** Shape only; {@link validatePrescriberDetailsInput} names an unknown jurisdiction. */
    state: usStateCodeSchema,
    /** Null when only the registration's state is known (backend2 never stored the number). */
    number: z.string().regex(DEA_NUMBER_PATTERN).nullable(),
  })
  .strict();
export type DeaRegistration = z.infer<typeof deaRegistrationSchema>;

/**
 * The read-side DEA entry. Deliberately not `.strict()`: it nests inside a
 * response a client parses (ADR-135), so a field added later must not break
 * an older Atlas bundle or an older Lambda reading a newer row.
 */
export const deaRegistrationReadSchema = z.object({
  state: z.string(),
  number: z.string().nullable(),
});

export const prescriberMailingAddressSchema = z.object({
  line1: z.string().nullable(),
  line2: z.string().nullable().optional(),
  locality: z.string().nullable(),
  region: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
});
export type PrescriberMailingAddress = z.infer<typeof prescriberMailingAddressSchema>;

/** The fields a US address must carry, named as the write route reports them. */
export const PRESCRIBER_ADDRESS_FIELDS = [
  'line1',
  'locality',
  'region',
  'postalCode',
  'country',
] as const;
export type PrescriberAddressField = (typeof PRESCRIBER_ADDRESS_FIELDS)[number];

const blank = (value: string | null | undefined): boolean =>
  value === null || value === undefined || value.trim() === '';

/** The first field that keeps a mailing address from being a complete US address, or null. */
export function firstIncompleteAddressField(
  address: PrescriberMailingAddress,
): PrescriberAddressField | null {
  if (!isUsCountry(address.country)) return 'country';
  if (blank(address.line1)) return 'line1';
  if (blank(address.locality)) return 'locality';
  if (blank(address.region) || !isPrescriberMailingRegion(address.region?.trim() ?? '')) {
    return 'region';
  }
  if (blank(address.postalCode) || !US_POSTAL_CODE_PATTERN.test(address.postalCode?.trim() ?? '')) {
    return 'postalCode';
  }
  return null;
}

// ─── read side ───────────────────────────────────────────────────────────────

export const PRESCRIBER_DETAILS_REFUSAL_REASON = [
  'specialist_not_found',
  'prescriber_details_absent',
  'npi_missing',
  'npi_invalid',
  'mailing_address_incomplete',
] as const;
export type PrescriberDetailsRefusalReason = (typeof PRESCRIBER_DETAILS_REFUSAL_REASON)[number];

export const prescriberDetailsSchema = z.object({
  specialistId: z.string().uuid(),
  npi: z.string().nullable(),
  deaRegistrations: z.array(deaRegistrationReadSchema),
  csrStates: z.array(z.string()),
  mailingAddress: prescriberMailingAddressSchema,
  /**
   * The staff address history row the mailing address came from, so whatever
   * ships against it can hold the row (ADR-130 Rule 3). Null when the address
   * came from the flat columns the history replaces, or there is none.
   */
  mailingAddressId: z.string().uuid().nullable().default(null),
  source: z.enum(PRESCRIBER_DETAILS_SOURCE),
  updatedAt: z.string().datetime({ offset: true }),
});
export type PrescriberDetails = z.infer<typeof prescriberDetailsSchema>;

/** The fields of a staff address history row the prescriber verdict reads. */
export interface PrescriberHistoryAddress {
  id: string;
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

/**
 * The mailing address a prescriber is judged on: the staff member's current
 * history row, or — only when they have none — the flat columns the history
 * replaces, which the previous release may still have written during its
 * deploy window. One definition for the staff routes and the pharmacy reader.
 */
export function mailingAddressFromSources(
  history: PrescriberHistoryAddress | null,
  flat: PrescriberMailingAddress,
): { mailingAddress: PrescriberMailingAddress; mailingAddressId: string | null } {
  if (history === null) return { mailingAddress: flat, mailingAddressId: null };
  return {
    mailingAddress: {
      line1: history.line1,
      line2: history.line2,
      locality: history.city,
      region: history.state,
      postalCode: history.postalCode,
      country: history.country,
    },
    mailingAddressId: history.id,
  };
}

export const prescriberDetailsVerdictSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('complete') }),
  z.object({ status: z.literal('refused'), reason: z.enum(PRESCRIBER_DETAILS_REFUSAL_REASON) }),
]);
export type PrescriberDetailsVerdict = z.infer<typeof prescriberDetailsVerdictSchema>;

/**
 * The verdict for stored details, `null` meaning the specialist exists but has
 * no prescriber row. An unknown specialist is the caller's to report — this
 * function never sees one.
 */
export function prescriberDetailsVerdict(
  details: Pick<PrescriberDetails, 'npi' | 'mailingAddress'> | null,
): PrescriberDetailsVerdict {
  if (details === null) return { status: 'refused', reason: 'prescriber_details_absent' };
  if (details.npi === null || details.npi.trim() === '') {
    return { status: 'refused', reason: 'npi_missing' };
  }
  // Present but failing the check digit: the database CHECK enforces only the
  // ten-digit shape, so a typo'd NPI can be stored, and it needs correcting
  // rather than finding.
  if (!isValidNpi(details.npi)) return { status: 'refused', reason: 'npi_invalid' };
  if (firstIncompleteAddressField(details.mailingAddress) !== null) {
    return { status: 'refused', reason: 'mailing_address_incomplete' };
  }
  return { status: 'complete' };
}

export const careStaffingPrescriberDetailsDataSchema = z.object({
  details: prescriberDetailsSchema.nullable(),
  verdict: prescriberDetailsVerdictSchema,
});
export type CareStaffingPrescriberDetailsData = z.infer<
  typeof careStaffingPrescriberDetailsDataSchema
>;

export const careStaffingPrescriberDetailsResponseSchema = apiEnvelope(
  careStaffingPrescriberDetailsDataSchema,
);
export type CareStaffingPrescriberDetailsResponse = z.infer<
  typeof careStaffingPrescriberDetailsResponseSchema
>;

// ─── write side ──────────────────────────────────────────────────────────────

/**
 * The staff write body. Shape only: the semantic rules (Luhn, jurisdiction,
 * address completeness) are applied by {@link validatePrescriberDetailsInput}
 * so the route can answer with a named code rather than a generic schema
 * failure.
 */
export const careStaffingPrescriberDetailsPutRequestSchema = z
  .object({
    /** Null or blank clears the NPI: a save never blocks on a missing one. */
    npi: z.string().max(32).nullable(),
    deaRegistrations: z
      .array(deaRegistrationSchema)
      .default([])
      .refine((rows) => new Set(rows.map((r) => r.state)).size === rows.length, {
        message: 'duplicate_state',
      }),
    csrStates: z.array(usStateCodeSchema).default([]),
    mailingAddress: z
      .object({
        line1: z.string().max(200),
        line2: z.string().max(200).nullable().optional(),
        locality: z.string().max(200),
        region: z.string().max(64),
        postalCode: z.string().max(32),
        country: z.string().max(64).nullable().optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type CareStaffingPrescriberDetailsPutRequest = z.infer<
  typeof careStaffingPrescriberDetailsPutRequestSchema
>;

/** The validated, normalised values a write stores. */
export interface PrescriberDetailsValues {
  npi: string | null;
  deaRegistrations: DeaRegistration[];
  csrStates: string[];
  /** Null when the write leaves the address alone. */
  mailingAddress: {
    line1: string;
    line2?: string | null;
    locality: string;
    region: string;
    postalCode: string;
    country: string | null;
  } | null;
}

/** Which DEA registration or CSR entry named an unknown jurisdiction, as a path into the body. */
export type PrescriberJurisdictionField =
  | `deaRegistrations[${number}].state`
  | `csrStates[${number}]`;

export type PrescriberDetailsValidation =
  | { ok: true; value: PrescriberDetailsValues }
  | { ok: false; code: 'invalid_npi' }
  | { ok: false; code: 'jurisdiction_unknown'; field: PrescriberJurisdictionField }
  | { ok: false; code: 'address_incomplete'; field: PrescriberAddressField };

/** The first DEA registration or CSR state outside {@link PRESCRIBER_JURISDICTIONS}, or null. */
export function firstUnknownJurisdictionField(input: {
  deaRegistrations: ReadonlyArray<{ state: string }>;
  csrStates: readonly string[];
}): PrescriberJurisdictionField | null {
  const dea = input.deaRegistrations.findIndex((r) => !isPrescriberJurisdiction(r.state));
  if (dea !== -1) return `deaRegistrations[${dea}].state`;
  const csr = input.csrStates.findIndex((state) => !isPrescriberJurisdiction(state));
  if (csr !== -1) return `csrStates[${csr}]`;
  return null;
}

export function validatePrescriberDetailsInput(
  input: CareStaffingPrescriberDetailsPutRequest,
): PrescriberDetailsValidation {
  const npiInput = input.npi?.trim() ?? '';
  const npi = npiInput === '' ? null : npiInput;
  if (npi !== null && !isValidNpi(npi)) return { ok: false, code: 'invalid_npi' };
  const jurisdictionField = firstUnknownJurisdictionField(input);
  if (jurisdictionField !== null) {
    return { ok: false, code: 'jurisdiction_unknown', field: jurisdictionField };
  }
  let address: PrescriberDetailsValues['mailingAddress'] = null;
  if (input.mailingAddress) {
    const line2Raw = input.mailingAddress.line2;
    const line2 =
      line2Raw === undefined
        ? undefined
        : line2Raw === null || line2Raw.trim() === ''
          ? null
          : line2Raw.trim();
    address = {
      line1: input.mailingAddress.line1.trim(),
      ...(line2 !== undefined ? { line2 } : {}),
      locality: input.mailingAddress.locality.trim(),
      // A full state name resolves to its code, as the pre-load does; anything
      // unresolvable stays as typed so the incomplete-address refusal names it.
      region:
        normaliseUsRegion(input.mailingAddress.region) ??
        input.mailingAddress.region.trim().toUpperCase(),
      postalCode: input.mailingAddress.postalCode.trim(),
      country: input.mailingAddress.country?.trim() || null,
    };
    const field = firstIncompleteAddressField(address);
    if (field !== null) return { ok: false, code: 'address_incomplete', field };
  }
  return {
    ok: true,
    value: {
      npi,
      deaRegistrations: input.deaRegistrations,
      csrStates: [...new Set(input.csrStates)],
      mailingAddress: address,
    },
  };
}
